import React, { useState, useMemo, useEffect, useRef, use } from "react";
import { Box, Stepper, Step, StepLabel, Card } from "@mui/material";
import { Formik, Form } from "formik";
import "./add-service.scss";
import ServiceInformation from "./service-information";
import Pricing from "./pricing-section";
import LocationAndMedia from "./location-media";
import { CreateServiceProps } from "../service-management-props";
import { routes } from "../../../../constants/routes";
import { useSetLoading } from "../../../../common/hooks/appearance";
import ProfileHeader from "../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import {
  initialValues,
  validationSchema,
  formatPayload,
  resolveSingleSelectionFlag,
} from "./add-service-helper";
import { StepIconProps } from "@mui/material/StepIcon";
import { AlertComponent } from "../../../../common/components";
import RightArrowIcon from ".././../../../assets/icons/right-grey-arrow.svg";
import InfoIcon from "../../../../assets/icons/info-icon.svg";
import { Tabs, Tab, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import {
  addService,
  updateService,
} from "../../../../api/provider-section/provider-service-management";
import { getProviderId } from "./add-service-helper";
import { getCategories } from "../../../../api/admin/service-management";
import { getServices } from "../../../../api/provider-section/provider-service-management";

const steps = ["Service Information", "Pricing", "Location & Media"];

const AddService: React.FC<CreateServiceProps> = ({
  setIsCreateServiceOpen,
  setAlertMessageForListPage,
  setIsAlertPopupOpenForListPage,
  setAlertTypeForListPage,
  setLoadingForListPage,
  actionType,
  currentProviderServiceId,
  fetchServices,
  setCurrentProviderServiceId,
  isCreateServiceOpen,
  setActionType,
  needtype,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [localActionType, setLocalActionType] = useState(actionType);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [serviceDetails, setServiceDetails] = useState<any>(null);
  const formRef = useRef<any>(null);
  const breadcrumbLinkForCreate = [
    {
      label: "Service Management",
      link: routes.SERVICE_LIST,
      handleClick: () => {
        handleCleanUp();
        setIsCreateServiceOpen(false);
      },
    },
    {
      label: "Service List",
      link: routes.SERVICE_LIST,
      handleClick: () => {
        handleCleanUp();
        setIsCreateServiceOpen(false);
      },
    },
    {
      label: "Add Service",
      link: "#",
      handleClick: () => {
        handleCleanUp();
        setIsCreateServiceOpen(false);
      },
    },
  ];
  const [breadcrumbLink, setBreadcrumbLink] = useState(breadcrumbLinkForCreate);
  const [formInitialValues, setFormInitialValues] = useState(initialValues);
  const [serviceCategories, setServiceCategories] = useState<any[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<
    Array<{ value: string; label: string; id: number }>
  >([]);
  const [selectedCategoryServicesList, setSelectedCategoryServicesList] =
    useState<Array<{ value: string; label: string; id: number }>>([]);
  const [subServiceOptions, setSubServiceOptions] = useState<
    Array<{ value: string; label: string; id: number }>
  >([]);
  const [merchandiseOptions, setMerchandiseOptions] = useState<
    Array<{ value: string; label: string; id: number }>
  >([]);
  // LIFTED STATE FOR PRICING SECTION
  const [locationOptions, setLocationOptions] = useState<any[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<any[]>([]);
  // Navigation handlers
  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const getFormKeyValueBasedOnStepValue = (
    step: number,
  ): "serviceInformation" | "pricingInformation" | "locationAndMedia" => {
    switch (step) {
      case 0:
        return "serviceInformation";
      case 1:
        return "pricingInformation";
      case 2:
        return "locationAndMedia";
      default:
        return "serviceInformation";
    }
  };

  const addServiceAPI = async (data: any) => {
    try {
      if (localActionType !== "create" && !currentProviderServiceId) {
        throw new Error("Service ID missing for update");
      }
      setLoading(true);
      const response = currentProviderServiceId
        ? await updateService(currentProviderServiceId, data)
        : await addService(data);
      if (response.data?.success) {
        // Reset form
        if (formRef.current) {
          formRef.current.resetForm();
        }
        setAlertMessageForListPage?.(
          `Service ${currentProviderServiceId ? "updated" : "added"} successfully.`,
        );
        setAlertTypeForListPage?.("success");
        setIsAlertPopupOpenForListPage?.(true);
        fetchServices?.();
        handleCleanUp();
        setIsCreateServiceOpen(false);
      }
    } catch (error: any) {
      console.error("Error adding service:", error);
      setAlertMessage(
        error?.response?.data?.message || "Failed to add service",
      );
      setAlertSeverity("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Form submission handler
  const handleFormSubmit = (values: any) => {
    if (subServiceOptions.length > 0) {
      const sub = Array.isArray(values?.serviceInformation?.subServiceIds) ? values.serviceInformation.subServiceIds : [];
      if (sub.length === 0) {
        formRef.current?.setFieldError('serviceInformation.subServiceIds', 'Select at least one Sub-service');
        formRef.current?.setFieldTouched('serviceInformation.subServiceIds', true, false);
        return;
      }
    }
    //get category id from category name
    const selectedCategory = serviceCategories.find(
      (cat) => cat.name === values.serviceInformation.serviceCategory,
    );
    if (selectedCategory) {
      values.serviceInformation.categoryId = selectedCategory.id;
    }
    const payload = formatPayload(values, values.serviceInformation.serviceId);
    addServiceAPI(payload);
  };

  const handleBackArrowClick = () => {
    setIsCreateServiceOpen(false);
    handleCleanUp();
  };

  const handleCleanUp = () => {
    setLocalActionType("create");
    setActionType?.("create");
    setActiveStep(0);
    setServiceDetails(null);
    setIsCreateServiceOpen(false);
    setBreadcrumbLink(breadcrumbLinkForCreate);
    setFormInitialValues(initialValues);
    //to reset the formik form
    if (formRef.current) {
      formRef.current.resetForm();
    }
    setCategoryOptions([]);
    setSelectedCategoryServicesList([]);
    setSubServiceOptions([]);
    setMerchandiseOptions([]);
    setLocationOptions([]);
    setSelectedLocations([]);
  };

  useEffect(() => {
    if (!isCreateServiceOpen) {
      handleCleanUp();
    }
  }, [isCreateServiceOpen]);

  useEffect(() => {
    if (actionType === "view" && isCreateServiceOpen) {
      setLocalActionType("view");
      setBreadcrumbLink([
        {
          label: "Service Management",
          link: routes.SERVICE_LIST,
          handleClick: () => {
            handleCleanUp();
            setIsCreateServiceOpen(false);
          },
        },
        {
          label: "Service List",
          link: routes.SERVICE_LIST,
          handleClick: () => {
            handleCleanUp();
            setIsCreateServiceOpen(false);
          },
        },
        {
          label: serviceDetails?.name || "View Service",
          link: "#",
          handleClick: () => {
            handleCleanUp();
            setIsCreateServiceOpen(false);
          },
        },
      ]);
    } else if (actionType === "edit") {
      setLocalActionType("edit");
      setBreadcrumbLink([
        {
          label: "Service Management",
          link: routes.SERVICE_LIST,
          handleClick: () => {
            handleCleanUp();
            setIsCreateServiceOpen(false);
          },
        },
        {
          label: "Service List",
          link: routes.SERVICE_LIST,
          handleClick: () => {
            handleCleanUp();
            setIsCreateServiceOpen(false);
          },
        },
        {
          label: "Edit Service",
          link: "#",
          handleClick: () => {
            handleCleanUp();
            setIsCreateServiceOpen(false);
          },
        },
      ]);
    } else {
      setBreadcrumbLink(breadcrumbLinkForCreate);
    }
  }, [actionType, isCreateServiceOpen, serviceDetails?.name]);

  const fetchCategories = async () => {
    try {

      let apiType = "all";

      if (needtype?.length === 1) {
        apiType = needtype[0]; // only one value present
      } else if (
        needtype?.includes("at_need") &&
        needtype?.includes("pre_need")
      ) {
        apiType = "all"; // both present
      }

      const response = await getCategories({ type: apiType });
      if (response.status === 200) {
        const categoriesData = response?.data?.data?.data || [];
        setServiceCategories(categoriesData);
        const formattedCategories = categoriesData?.map((category: any) => ({
          value: category?.name,
          label: category?.name,
          id: category?.id,
        }));
        setCategoryOptions(formattedCategories);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    if (isCreateServiceOpen) fetchCategories();
  }, [isCreateServiceOpen]);

  const getServiceType = (type: string) => {
    switch (type) {
      case "sub_service":
        return "Sub-service";
      case "merchandise":
        return "Merchandise";
      default:
        return "Service";
    }
  };
  //const fetch single service details
  const fetchServiceDetails = async (id: number) => {
    setLoading(true);
    try {
      const response = await getServices({}, id);
      if (response.status === 200) {
        const resData = response?.data?.data;
        const formattedValues = {
          serviceInformation: {
            providerId: getProviderId(),
            //concat categories names array
            serviceCategory: resData?.service?.categories[0]?.name || "",
            serviceName: resData?.service?.name || "",
            serviceCode: resData?.service?.code || "",
            serviceShortDescription: resData?.descriptions?.short || "",
            serviceDetailedDescription:
              resData?.descriptions?.detail || "",
            duration: resData?.duration || "",
            availability: resData?.status === "active" ? true : false,
            needType:
              (resData?.service?.needType ||
                resData?.service?.needtype ||
                resData?.needType ||
                resData?.needtype) === "pre_need"
                ? "Preneed"
                : (resData?.service?.needType ||
                  resData?.service?.needtype ||
                  resData?.needType ||
                  resData?.needtype) === "at_need"
                  ? "Immediate Need"
                  : (resData?.service?.needType ||
                    resData?.service?.needtype ||
                    resData?.needType ||
                    resData?.needtype) || "-",
            serviceId: resData?.service?.id || null,
            serviceType: resData?.service?.type || "",
            subServiceIds: [],
            merchandiseIds: [],
            singleSubServiceSelection: resolveSingleSelectionFlag(
              resData,
              "subService",
            ),
            singleMerchandiseSelection: resolveSingleSelectionFlag(
              resData,
              "merchandise",
            ),
          },
          pricingInformation: {
            additionalFee: resData?.additionalFee || 0,
            insuranceSupport:
              resData?.insuranceSupport !== undefined
                ? resData?.insuranceSupport
                : true,
            items:
              resData?.pricing
              ?.filter((item: any) => item?.providerPrice !== undefined && item?.providerPrice !== null)
              ?.map((item: any) => ({
                id: item?.id || null,
                name: item?.name || "",
                description: item?.description || "",
                price: item?.providerPrice || 0,
                minPrice: item?.minPrice || 0,
                maxPrice: item?.maxPrice || 0,
                type: getServiceType(item?.type),
                selected: true,
              })) || [],
            acceptedPaymentMethod:
              resData?.acceptedPaymentMethod || "online",
          },
          locationAndMedia: {
            locations: resData?.serviceableLocations || [],
          },
        };
        if (formattedValues.serviceInformation.serviceName) {
          //set selected sub services and merchandise based on fetched data
          //in pricing we have array of items on that we have type field to identify sub_service and merchandise
          const selectedSubServices =
            resData?.pricing
              ?.filter((item: any) => item.type === "sub_service" &&item.providerPrice !== undefined)
              .map((subService: any) => subService?.name) || [];
          const selectedMerchandise =
            resData?.pricing
              ?.filter((item: any) => item.type === "merchandise" && item.providerPrice !== undefined)
              .map((merchandise: any) => merchandise?.name) || [];
          //set these values to form initial values
          formattedValues.serviceInformation.subServiceIds =
            selectedSubServices;
          formattedValues.serviceInformation.merchandiseIds =
            selectedMerchandise;
        }
        //set selected locations
        if (formattedValues.locationAndMedia.locations.length > 0) {
          setSelectedLocations(
            formattedValues.locationAndMedia.locations.map((loc: any) => ({
              value: `${loc.city}, ${loc.state}, ${loc.zipcode}`,
              label: `${loc.city}, ${loc.state}, ${loc.zipcode}`,
              id: loc.id,
              city: loc.city,
              state: loc.state,
              zipcode: loc.zipcode,
            })),
          );
        } else {
          setSelectedLocations([]);
        }
        setFormInitialValues(formattedValues);

        // Build serviceDetails so PricingSection's useEffect can correctly
        // reconstruct pricingInformation.items without overwriting them with empty data.
        const mainPricingItem = resData?.pricing?.find(
          (item: any) => item.type === "service" || item.type === "Service",
        );
        const subServicePricingItems =
          resData?.pricing?.filter(
            (item: any) =>
              item.type === "sub_service" || item.type === "Sub-service",
          ) || [];
        const merchandisePricingItems =
          resData?.pricing?.filter(
            (item: any) =>
              item.type === "merchandise" || item.type === "Merchandise",
          ) || [];

        setServiceDetails({
          id: mainPricingItem?.id || resData?.service?.id,
          name: resData?.service?.name || "",
          category: resData?.service?.categories?.[0]?.name || "",
          singleSubServiceSelection: resolveSingleSelectionFlag(
            resData,
            "subService",
          ),
          singleMerchandiseSelection: resolveSingleSelectionFlag(
            resData,
            "merchandise",
          ),
          minPrice: mainPricingItem?.minPrice || 0,
          maxPrice: mainPricingItem?.maxPrice || 0,
          providerPrice: mainPricingItem?.providerPrice || 0,
          subServices: subServicePricingItems.map((item: any) => ({
            id: item.id,
            name: item.name,
            minPrice: item.minPrice || 0,
            maxPrice: item.maxPrice || 0,
            providerPrice: item.providerPrice || 0,
          })),
          merchandises: merchandisePricingItems.map((item: any) => ({
            id: item.id,
            name: item.name,
            minPrice: item.minPrice || 0,
            maxPrice: item.maxPrice || 0,
            providerPrice: item.providerPrice || 0,
          })),
        });
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch service details.",
      );
      setAlertSeverity("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      currentProviderServiceId &&
      (actionType === "edit" || actionType === "view")
    ) {
      fetchServiceDetails(currentProviderServiceId);
    }
  }, [currentProviderServiceId, actionType]);

  // Loading state
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  // Step/Tab component renderer
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <ServiceInformation
            setLoading={setLoading}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertSeverity}
            setAlertOpen={setAlertOpen}
            serviceDetails={serviceDetails}
            setServiceDetails={setServiceDetails}
            isReadOnly={localActionType === "view"}
            serviceCategories={serviceCategories}
            setServiceCategories={setServiceCategories}
            categoryOptions={categoryOptions}
            setCategoryOptions={setCategoryOptions}
            selectedCategoryServicesList={selectedCategoryServicesList}
            setSelectedCategoryServicesList={setSelectedCategoryServicesList}
            subServiceOptions={subServiceOptions}
            setSubServiceOptions={setSubServiceOptions}
            merchandiseOptions={merchandiseOptions}
            setMerchandiseOptions={setMerchandiseOptions}
            actionType={localActionType}
          />
        );
      case 1:
        return (
          <Pricing
            setLoading={setLoading}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertSeverity}
            setAlertOpen={setAlertOpen}
            serviceDetails={serviceDetails}
            setServiceDetails={setServiceDetails}
            isReadOnly={localActionType === "view"}
            actionType={localActionType}
          />
        );
      case 2:
        return (
          <LocationAndMedia
            setLoading={setLoading}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertSeverity}
            setAlertOpen={setAlertOpen}
            isReadOnly={localActionType === "view"}
            actionType={localActionType}
            locationOptions={locationOptions}
            setLocationOptions={setLocationOptions}
            selectedLocations={selectedLocations}
            setSelectedLocations={setSelectedLocations}
          />
        );
      default:
        return null;
    }
  };

  const CustomStepIcon: React.FC<StepIconProps> = (props) => {
    const { active, completed, icon } = props;

    return (
      <div
        className={`custom-step-icon ${active ? "active" : ""} ${completed ? "completed" : ""}`}
      >
        <span>{icon}</span>
      </div>
    );
  };

  return (
    <div className="create-service-main-wrapper provider-add-service-main-wrapper">
      <ProfileHeader
        breadcrumbLink={breadcrumbLink}
        showBackArrow
        handleBackArrowClick={handleBackArrowClick}
        mode="provider"
      />

      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize={true}
        validateOnChange={true}
        validateOnBlur={true}
        innerRef={formRef}
      >
        {({
          values,
          errors,
          touched,
          isValid,
          isSubmitting,
          setFieldError,
          dirty,
          resetForm,
        }) => {
          return (
            <Form>
              <Box className="stepper-and-content-wrapper">
                {/* Stepper or Tabs */}
                {localActionType === "view" || localActionType === "edit" ? (
                  <Box
                    className="tabs-wrapper"
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "20px",
                      position: "relative",
                    }}
                  >
                    <Tabs
                      value={activeStep}
                      onChange={(_, newValue) => setActiveStep(newValue)}
                      aria-label="Service Tabs"
                      sx={{
                        alignItems: "flex-start",
                        ".MuiTabs-flexContainer": {
                          alignItems: "flex-start",
                          gap: "20px",
                        },
                      }}
                    >
                      {steps.map((label, index) => (
                        <Tab
                          key={label}
                          label={label}
                          sx={{ alignItems: "flex-start" }}
                        />
                      ))}
                    </Tabs>
                    {localActionType === "view" && (
                      <IconButton
                        aria-label="Edit"
                        sx={{ alignSelf: "flex-start", marginLeft: "auto" }}
                        onClick={() => {
                          setLocalActionType("edit")
                          setActionType?.("edit");
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </Box>
                ) : (
                  <Box className="stepper-wrapper">
                    <Stepper
                      activeStep={activeStep}
                      sx={{
                        "& .MuiStepConnector-line": {
                          borderColor: activeStep >= 2 ? "#176c73" : "#c9c9c9",
                          borderWidth: activeStep >= 2 ? "2px" : "1px",
                          background:
                            activeStep >= 2
                              ? "linear-gradient(90deg, #176c73 0%, #2e8b90 100%)"
                              : "transparent",
                        },
                        "& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line":
                        {
                          borderColor:
                            activeStep >= 2 ? "#176c73" : "#c9c9c9",
                          background:
                            activeStep >= 2
                              ? "linear-gradient(90deg, #176c73 0%, #2e8b90 100%)"
                              : "transparent",
                        },
                      }}
                    >
                      {steps.map((label, index) => (
                        <Step key={index} completed={activeStep > index}>
                          <StepLabel StepIconComponent={CustomStepIcon}
                            style={{ cursor: index < activeStep ? "pointer" : index === activeStep ? "default" : "pointer" }}
                            onClick={(e) => {
                              e.preventDefault();
                              if (index < activeStep) {
                                setActiveStep(index);
                              }
                            }}
                          >
                            {label}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>
                )}

                {/* Content Card */}
                <Card className="step-card content-card">
                  {/* Step/Tab Content */}
                  <Box className="step-content">
                    {renderStepContent(activeStep)}
                    {/* Unified Footer for both tabs and stepper */}
                    <div className="footer-section">
                      <div className="buttons-wrapper">
                        {/* Back button for all modes */}
                        {activeStep > 0 && (
                          <button
                            type="button"
                            className="cancel-button"
                            onClick={() => {
                              handleBack();
                            }}
                          // disabled={
                          //   Object.keys(
                          //     errors?.[
                          //       getFormKeyValueBasedOnStepValue(activeStep)
                          //     ] || {},
                          //   ).length > 0
                          // }
                          >
                            Back
                          </button>
                        )}
                        {/* Next button for all modes except last step/tab */}
                        {activeStep < steps.length - 1 && (
                          <button
                            type="button"
                            className="submit-button"
                            onClick={async () => {
                              if (activeStep === 0) {
                                // Step 1: Service Information - Sub-service is mandatory when options exist
                                const values = formRef.current?.values;
                                const sub = Array.isArray(values?.serviceInformation?.subServiceIds) ? values.serviceInformation.subServiceIds : [];
                                const hasSubServiceOptions = subServiceOptions.length > 0;
                                if (hasSubServiceOptions && sub.length === 0) {
                                  formRef.current?.setFieldError('serviceInformation.subServiceIds', 'Select at least one Sub-service');
                                  formRef.current?.setFieldTouched('serviceInformation.subServiceIds', true, false);
                                  return;
                                }
                                if (!hasSubServiceOptions || sub.length > 0) {
                                  handleNext();
                                }
                              } else {
                                handleNext();
                              }
                            }}
                            disabled={
                              activeStep === 0
                                ? (() => {
                                  const stepErrors = Object.keys(errors?.serviceInformation || {}).length > 0;
                                  const sub = Array.isArray(values?.serviceInformation?.subServiceIds) ? values.serviceInformation.subServiceIds : [];
                                  const subRequiredButEmpty = subServiceOptions.length > 0 && sub.length === 0;
                                  return stepErrors || subRequiredButEmpty;
                                })()
                                : activeStep === 1
                                  ? (() => {
                                    // Pricing step: require at least one selected and all required prices valid
                                    const items = values?.pricingInformation?.items || [];
                                    const atLeastOneSelected = items.some((item: any) => item.selected);
                                    const pricingErrors = (errors?.pricingInformation as any)?.items;
                                    let hasPriceError = false;
                                    if (Array.isArray(pricingErrors)) {
                                      hasPriceError = items.some((item: any, idx: number) => item.selected && pricingErrors[idx] && (pricingErrors[idx] as any).price);
                                    }
                                    return !atLeastOneSelected || hasPriceError;
                                  })()
                                  : Object.keys(
                                    errors?.[
                                    getFormKeyValueBasedOnStepValue(activeStep)
                                    ] || {},
                                  ).length > 0
                            }
                          >
                            Next
                            <img
                              src={RightArrowIcon}
                              alt="right arrow"
                              className="right-arrow-icon"
                            />
                          </button>
                        )}
                        {/* Save button for last step/tab */}
                        {activeStep === steps.length - 1 && (
                          <button
                            type="submit"
                            className="submit-button"
                            disabled={localActionType === "view"}
                          >
                            {"Save"}
                          </button>
                        )}
                      </div>
                      <p className="footer-note">
                        <img
                          src={InfoIcon}
                          alt="info icon"
                          className="info-icon"
                        />
                        {activeStep === steps.length - 1
                          ? "Fill all mandatory fields to save"
                          : "Fill all mandatory fields to go to next step"}
                      </p>
                    </div>
                  </Box>
                </Card>
              </Box>
            </Form>
          );
        }}
      </Formik>

      {/* Alert Snackbar */}
      <AlertComponent
        open={alertOpen}
        message={alertMessage}
        type={alertSeverity}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default AddService;
